import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import EmptyState from '../components/EmptyState';

interface NationalStats {
  total_libraries: number;
  total_books: number;
  total_holdings: number;
  active_libraries: number;
  libraries_with_stats: number;
}

interface PipelineJobRecord {
  id: number;
  command: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  trigger: 'manual' | 'cron';
  triggered_by: { name: string } | null;
  log_output: string | null;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface PipelineStatusData {
  active_jobs: PipelineJobRecord[];
  last_completed: PipelineJobRecord | null;
}

interface StorageStats {
  database: {
    total_bytes: number;
    data_bytes: number;
    index_bytes: number;
    tables: { name: string; row_count: number; size_bytes: number }[];
  };
  r2_archives: {
    archive_count: number;
    latest_archive: string | null;
  };
}

const COMMANDS = ['ingest', 'stats', 'enrich', 'reindex', 'flag-anomalies'] as const;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function duration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  return `${mins}m ${remSecs}s`;
}

export default function PipelineStatus() {
  const [stats, setStats] = useState<NationalStats | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatusData | null>(null);
  const [jobs, setJobs] = useState<PipelineJobRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [expandedJob, setExpandedJob] = useState<number | null>(null);
  const [jobLog, setJobLog] = useState<string | null>(null);
  const [storage, setStorage] = useState<StorageStats | null>(null);

  const loadData = useCallback(() => {
    api.get<{ data: NationalStats }>('/stats/national')
      .then((res) => setStats(res.data))
      .catch(() => {});

    api.get<{ data: PipelineStatusData }>('/pipeline/status')
      .then((res) => setPipelineStatus(res.data))
      .catch(() => {});

    api.get<{ data: PipelineJobRecord[] }>('/pipeline/jobs')
      .then((res) => setJobs(res.data))
      .catch(() => {});

    api.get<{ data: StorageStats }>('/stats/storage')
      .then((res) => setStorage(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRun = async (command: string) => {
    setError(null);
    setSubmitting(command);
    try {
      await api.post('/pipeline/run', { command });
      loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(null);
    }
  };

  const activeCommands = new Set(
    (pipelineStatus?.active_jobs || []).map((j) => j.command)
  );

  const toggleLog = async (job: PipelineJobRecord) => {
    if (expandedJob === job.id) {
      setExpandedJob(null);
      setJobLog(null);
      return;
    }
    setExpandedJob(job.id);
    if (job.log_output) {
      setJobLog(job.log_output);
    } else {
      try {
        const res = await api.get<{ data: PipelineJobRecord }>(`/pipeline/jobs/${job.id}`);
        setJobLog(res.data.log_output || 'No log output.');
      } catch {
        setJobLog('Failed to load log.');
      }
    }
  };

  return (
    <div>
      <h1>Pipeline</h1>

      <div className="card">
        <h2>Run Pipeline</h2>
        {error && <p className="error">{error}</p>}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {COMMANDS.map((cmd) => {
            const isActive = activeCommands.has(cmd);
            return (
              <button
                key={cmd}
                onClick={() => handleRun(cmd)}
                disabled={isActive || submitting !== null}
              >
                {isActive
                  ? `${cmd} running...`
                  : submitting === cmd
                  ? 'Queuing...'
                  : `Run ${cmd}`}
              </button>
            );
          })}
        </div>
        {pipelineStatus?.last_completed && (
          <p style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.85rem' }}>
            Last completed: <strong>{pipelineStatus.last_completed.command}</strong>{' '}
            at {formatTime(pipelineStatus.last_completed.completed_at)}
          </p>
        )}
      </div>

      {stats && (
        <div className="card">
          <h2>Database Totals</h2>
          <dl>
            <dt>Libraries</dt><dd>{stats.total_libraries.toLocaleString()}</dd>
            <dt>Active Libraries</dt><dd>{stats.active_libraries.toLocaleString()}</dd>
            <dt>Books</dt><dd>{stats.total_books.toLocaleString()}</dd>
            <dt>Holdings</dt><dd>{stats.total_holdings.toLocaleString()}</dd>
            <dt>Libraries with Stats</dt><dd>{stats.libraries_with_stats.toLocaleString()}</dd>
          </dl>
        </div>
      )}

      {storage && (
        <div className="card">
          <h2>Storage Usage</h2>
          <dl>
            <dt>Database Total</dt><dd>{formatBytes(storage.database.total_bytes)}</dd>
            <dt>Data</dt><dd>{formatBytes(storage.database.data_bytes)}</dd>
            <dt>Indexes</dt><dd>{formatBytes(storage.database.index_bytes)}</dd>
            <dt>R2 Archives</dt><dd>{storage.r2_archives.archive_count.toLocaleString()} snapshots</dd>
            {storage.r2_archives.latest_archive && (
              <><dt>Latest Archive</dt><dd>{formatTime(storage.r2_archives.latest_archive)}</dd></>
            )}
          </dl>
          <details style={{ marginTop: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#666' }}>Table breakdown</summary>
            <table className="data-table" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
              <thead>
                <tr><th>Table</th><th>Rows</th><th>Size</th></tr>
              </thead>
              <tbody>
                {storage.database.tables.map((t) => (
                  <tr key={t.name}>
                    <td>{t.name}</td>
                    <td>{t.row_count.toLocaleString()}</td>
                    <td>{formatBytes(t.size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      <div className="card">
        <h2>Job History</h2>
        {jobs.length === 0 ? (
          <EmptyState
            title="No pipeline jobs yet"
            description="Use the buttons above to run your first pipeline command."
          />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Command</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Queued</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <React.Fragment key={job.id}>
                  <tr
                    onClick={() => toggleLog(job)}
                    style={{ cursor: 'pointer' }}
                    className={expandedJob === job.id ? 'row-expanded' : ''}
                  >
                    <td>{job.command}</td>
                    <td>
                      <span className={`status-${job.status}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>
                      {job.trigger === 'manual' && job.triggered_by
                        ? job.triggered_by.name
                        : job.trigger}
                    </td>
                    <td>{formatTime(job.queued_at)}</td>
                    <td>{duration(job.started_at, job.completed_at)}</td>
                  </tr>
                  {expandedJob === job.id && (
                    <tr>
                      <td colSpan={5}>
                        <pre className="job-log">{jobLog || 'Loading...'}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
